import { Dispatch, useCallback, useEffect, useState } from 'react';
import { FormRenderProps } from 'react-final-form';
import equal from 'fast-deep-equal';
import { connect, useDispatch } from 'react-redux';

import { FiltersModal } from 'CorePackage/FiltersModalModule/components/FiltersModal';
import filtersModalStyles from 'CorePackage/FiltersModalModule/components/FiltersModal.module.scss';
import { composeValidators } from 'CorePackage/MainModule/validators/composeValidators';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRiskCategorySelectOption } from 'CorePackage/CatalogsModule/interfaces/IRiskCategorySelectOption';
import { IDictionaryEventTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryEventTypeSelectOption';
import { IDictionaryKnmKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionaryKnmKindSelectOption';
import { IDictionarySubjectKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectKindSelectOption';
import { IDictionarySubjectTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectTypeSelectOption';
import { IDictionarySupervisionTypeSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySupervisionTypeSelectOption';
import { IKnmListByPlanFilterWithMeta } from 'KnmListByPlanPackage/MainModule/interfaces/IKnmListByPlanFilterWithMeta';
import { IMspCategorySelectOption } from 'CorePackage/CatalogsModule/interfaces/IMspCategorySelectOption';
import {
  getCatalogRiskCategoriesSelectOptions,
  getCatalogsMspCategoriesSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IDictionarySubjectSubKindSelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySubjectSubKindSelectOption';
import {
  getDictionariesAllEventTypesSelectOptions,
  getDictionariesAllKnmKindsSelectOptions,
} from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  getKnmListByPlanCurrent,
  getKnmListByPlanCurrentId,
  getKnmListByPlanFilters,
  getKnmListByPlanSubjectKindsSelectOptions,
  getKnmListByPlanSubjectSubKindsSelectOptions,
  getKnmListByPlanSubjectTypesSelectOptions,
  getKnmListByPlanSupervisionTypesSelectOptions,
} from 'KnmListByPlanPackage/MainModule/selectors/knmListByPlanSelectors';
import { IFilterResult } from 'APIPackage/MainModule/interfaces/IFilterResult';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import {
  IKnmListByPlanFilterSetCurrentParams,
  knmListByPlanFilterCreate,
  knmListByPlanFilterGetData,
  knmListByPlanFilterRemove,
  knmListByPlanFilterSetCurrent,
  knmListByPlanFilterSetSwitcher,
  knmListByPlanFilterUpdate,
  knmListByPlanGetData,
  knmListByPlanSetSupervisionTypeDictVersionId,
} from 'KnmListByPlanPackage/MainModule/actions/knmListByPlanActions';

import { mapFilterAdditionalFields } from '../common/mapFilterAdditionalFields';
import { initialKnmListByPlanFilterAdditionalFields } from '../constants/initialKnmListByPlanFilterAdditionalFields';
import { initialKnmListByPlanFilter } from '../constants/initialKnmListByPlanFilter';
import { knmListByPlanBooleanSelectOptions } from '../constants/knmListByPlanBooleanSelectOptions';
import { IKnmListByPlanFilter } from '../interfaces/IKnmListByPlanFilter';
import { IKnmListByPlanFilterExtraField } from '../interfaces/IKnmListByPlanFilterExtraField';
import { IKnmListByPlanFiltersForm } from '../interfaces/IKnmListByPlanFiltersForm';

interface IKnmListByPlanFiltersModalProps {
  visible: boolean;
  onClose: () => void;
}

interface IKnmListByPlanFiltersModalStoreProps {
  riskCategoriesSelectOptions: IRiskCategorySelectOption[];
  mspCategoriesSelectOptions: IMspCategorySelectOption[];
  supervisionTypesSelectOptions: IDictionarySupervisionTypeSelectOption[] | undefined;
  objectTypesSelectOptions: IDictionarySubjectTypeSelectOption[] | undefined;
  objectKindsSelectOptions: IDictionarySubjectKindSelectOption[] | undefined;
  knmKindsSelectOptions: IDictionaryKnmKindSelectOption[] | undefined;
  eventTypesSelectOptions: IDictionaryEventTypeSelectOption[] | undefined;
  filters: IKnmListByPlanFilterWithMeta[];
  currentFilter: IKnmListByPlanFilter | undefined;
  activeFilterId: number | undefined;
  objectSubKindsSelectOptions: IDictionarySubjectSubKindSelectOption[] | undefined;
}

const KnmListByPlanFiltersModalWithoutStore = ({
  visible,
  activeFilterId,
  currentFilter,
  eventTypesSelectOptions,
  filters,
  knmKindsSelectOptions,
  mspCategoriesSelectOptions,
  objectKindsSelectOptions,
  objectSubKindsSelectOptions,
  objectTypesSelectOptions,
  riskCategoriesSelectOptions,
  supervisionTypesSelectOptions,
  onClose,
}: IKnmListByPlanFiltersModalProps & IKnmListByPlanFiltersModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const getKnmListByPlanData = useCallback(() => dispatch(knmListByPlanGetData()), [dispatch]);
  const getKnmListByPlanFilterData = useCallback(
    (id: IFilterResult['id']) => dispatch(knmListByPlanFilterGetData(id)),
    [dispatch],
  );
  const setKnmListByPlanFilterCurrent = useCallback(
    (params: IKnmListByPlanFilterSetCurrentParams) => dispatch(knmListByPlanFilterSetCurrent(params)),
    [dispatch],
  );
  const setKnmListByPlanFilterSwitcher = useCallback(
    (value: boolean) => dispatch(knmListByPlanFilterSetSwitcher(value)),
    [dispatch],
  );
  const createFilter = useCallback((data: IKnmListByPlanFilter) => dispatch(knmListByPlanFilterCreate(data)), [
    dispatch,
  ]);
  const updateFilter = useCallback(
    (id: IFilterResult['id'], data: IKnmListByPlanFilter) => dispatch(knmListByPlanFilterUpdate(id, data)),
    [dispatch],
  );
  const removeFilter = useCallback((id: IFilterResult['id']) => dispatch(knmListByPlanFilterRemove(id)), [dispatch]);
  const setSupervisionTypeDictVersionId = useCallback(
    (supervisionTypeDictVersionId: string | undefined) =>
      dispatch(knmListByPlanSetSupervisionTypeDictVersionId(supervisionTypeDictVersionId)),
    [dispatch],
  );

  const [supervisionTypeDictVersionIdValue, setSupervisionTypeDictVersionIdValue] = useState<string>();
  const [additionalFields, setAdditionalFields] = useState(initialKnmListByPlanFilterAdditionalFields);
  const [formValues, setFormValues] = useState<IKnmListByPlanFiltersForm>(initialKnmListByPlanFilter);

  useEffect(() => {
    setSupervisionTypeDictVersionId(supervisionTypeDictVersionIdValue);
  }, [setSupervisionTypeDictVersionId, supervisionTypeDictVersionIdValue]);

  const onTemplateCreate = useCallback(() => {
    setFormValues(initialKnmListByPlanFilter);
    setAdditionalFields(initialKnmListByPlanFilterAdditionalFields);
  }, []);

  const mapFilter = useCallback((filter: IKnmListByPlanFilter, filterName: string): IKnmListByPlanFilter => {
    return {
      ...filter,
      name: filterName,
    };
  }, []);

  const removeAdditionalField = (name: IKnmListByPlanFilterExtraField['id']) => {
    setFormValues(prev => {
      if (name === 'startDateBetween') {
        return {
          ...prev,
          startDateBetweenStart: initialKnmListByPlanFilter.startDateBetweenStart,
          startDateBetweenEnd: initialKnmListByPlanFilter.startDateBetweenEnd,
        };
      }

      return {
        ...prev,
        [name]: initialKnmListByPlanFilter[name],
      };
    });
    setAdditionalFields(prev =>
      prev.map(f => {
        if (f.id === name) {
          return {
            ...f,
            isSelected: false,
          };
        }

        return f;
      }),
    );
  };

  function renderFormContent({ form, values }: FormRenderProps<IKnmListByPlanFiltersForm>) {
    const supervisionTypeDictVersionId = values.supervisionType?.value.dictVersionId;

    setSupervisionTypeDictVersionIdValue(supervisionTypeDictVersionId);

    const makeRemoveFilterHandler = (name: IKnmListByPlanFilterExtraField['id']) => () => {
      removeAdditionalField(name);
      if (name === 'startDateBetween') {
        form.change('startDateBetweenStart');
        form.resetFieldState('startDateBetweenStart');
        form.change('startDateBetweenEnd');
        form.resetFieldState('startDateBetweenEnd');
      } else {
        form.change(name);
        form.resetFieldState(name);
      }
    };

    const getReasonDateBetweenStartValue = () => form.getState().values.reasonDateBetweenStart;
    const getReasonDateBetweenEndValue = () => form.getState().values.reasonDateBetweenEnd;
    const getStartDateBetweenStartValue = () => form.getState().values.startDateBetweenStart;
    const getStartDateBetweenEndValue = () => form.getState().values.startDateBetweenEnd;

    const primaryContent = (
      <>
        <Row border="bottom">
          <FieldBlock label="Категория риска" labelFontSize="small">
            <SelectInputField
              id="riskCategory"
              name="riskCategory"
              size="medium"
              options={riskCategoriesSelectOptions}
              isClearable
              isMulti
            />
          </FieldBlock>
        </Row>

        <Row border="bottom">
          <FieldBlock label="Дата основания включения в план (интервал)" labelFontSize="small">
            <div className={filtersModalStyles.FilterFormGroupDatePickers}>
              <span className={filtersModalStyles.FilterFormGroupDatePickersLabel}>с</span>
              <DatePickerField
                className={filtersModalStyles.FilterFormGroupDatePickersItem}
                name="reasonDateBetweenStart"
                isEqual={equal}
                validate={makeNoLaterThenValidator(
                  'Дата начала должна быть меньше или равна дате окончания',
                  getReasonDateBetweenEndValue,
                )}
              />
              <span className={filtersModalStyles.FilterFormGroupDatePickersLabelNotFirst}>по</span>
              <DatePickerField
                className={filtersModalStyles.FilterFormGroupDatePickersItem}
                name="reasonDateBetweenEnd"
                isEqual={equal}
                validate={makeNoSoonerThenValidator(
                  'Дата окончания должна быть больше или равна дате начала',
                  getReasonDateBetweenStartValue,
                )}
              />
            </div>
          </FieldBlock>
        </Row>

        <Row>
          <FieldBlock label="Категория субъекта МСП" labelFontSize="small">
            <SelectInputField
              id="mspCategory"
              name="mspCategory"
              size="medium"
              options={mspCategoriesSelectOptions}
              isClearable
              isMulti
            />
          </FieldBlock>
        </Row>
      </>
    );

    const secondaryContent = (
      <>
        {additionalFields
          .filter(f => f.isSelected)
          .map((field, index) => (
            <Row
              key={field.id}
              border={index < additionalFields.filter(f => f.isSelected).length - 1 ? 'bottom' : undefined}
            >
              <FieldBlock
                label={field.id !== 'signed' ? field.name : ' '}
                type="flex-label"
                button={<CloseButton onClick={makeRemoveFilterHandler(field.id)} />}
              >
                {field.id === 'supervisionType' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    size="medium"
                    options={supervisionTypesSelectOptions}
                    isSearchable
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
                {field.id === 'objectsType' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    size="medium"
                    options={objectTypesSelectOptions}
                    isSearchable
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
                {field.id === 'objectsKind' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    size="medium"
                    options={objectKindsSelectOptions}
                    isSearchable
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
                {field.id === 'objectsSubKind' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    size="medium"
                    options={objectSubKindsSelectOptions}
                    isSearchable
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
                {field.id === 'isHasRequirements' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    size="medium"
                    isClearable={false}
                    options={knmListByPlanBooleanSelectOptions}
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
                {field.id === 'isHasApproveDocs' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    size="medium"
                    isClearable={false}
                    options={knmListByPlanBooleanSelectOptions}
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
                {field.id === 'startDateBetween' && (
                  <div className={filtersModalStyles.FilterFormGroupDatePickers}>
                    <span className={filtersModalStyles.FilterFormGroupDatePickersLabel}>с</span>
                    <DatePickerField
                      className={filtersModalStyles.FilterFormGroupDatePickersItem}
                      name="startDateBetweenStart"
                      isEqual={equal}
                      validate={composeValidators(
                        makeRequired('Поле не может быть пустым'),
                        makeNoLaterThenValidator(
                          'Дата начала должна быть меньше или равна дате окончания',
                          getStartDateBetweenEndValue,
                        ),
                      )}
                    />
                    <span className={filtersModalStyles.FilterFormGroupDatePickersLabelNotFirst}>по</span>
                    <DatePickerField
                      className={filtersModalStyles.FilterFormGroupDatePickersItem}
                      name="startDateBetweenEnd"
                      isEqual={equal}
                      validate={composeValidators(
                        makeRequired('Поле не может быть пустым'),
                        makeNoSoonerThenValidator(
                          'Дата окончания должна быть больше или равна дате начала',
                          getStartDateBetweenStartValue,
                        ),
                      )}
                    />
                  </div>
                )}
                {field.id === 'kinds' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    size="medium"
                    options={knmKindsSelectOptions}
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
                {field.id === 'events' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    size="medium"
                    options={eventTypesSelectOptions}
                    isClearable
                    isMulti
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
                {field.id === 'isHasCollaboratingOrganization' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    size="medium"
                    isClearable={false}
                    options={knmListByPlanBooleanSelectOptions}
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
                {field.id === 'signed' && (
                  <CheckboxField id={field.id} name={field.id} theme="system" label={field.name} type="checkbox" />
                )}
              </FieldBlock>
            </Row>
          ))}
      </>
    );

    return {
      primaryContent,
      secondaryContent,
    };
  }

  return (
    <FiltersModal<IKnmListByPlanFiltersForm, IKnmListByPlanFilter>
      visible={visible}
      initialFormValues={formValues}
      additionalFields={additionalFields}
      filters={filters}
      currentFilter={currentFilter}
      activeFilterId={activeFilterId}
      renderFormContent={renderFormContent}
      mapFilter={mapFilter}
      mapFilterAdditionalFields={mapFilterAdditionalFields}
      onClose={onClose}
      onTemplateCreate={onTemplateCreate}
      setFormValues={setFormValues}
      setAdditionalFields={setAdditionalFields}
      getData={getKnmListByPlanData}
      getFilterData={getKnmListByPlanFilterData}
      setFilterCurrent={setKnmListByPlanFilterCurrent}
      setFilterSwitcher={setKnmListByPlanFilterSwitcher}
      createFilter={createFilter}
      updateFilter={updateFilter}
      removeFilter={removeFilter}
    />
  );
};

const mapStateToProps = (state: IRootState): IKnmListByPlanFiltersModalStoreProps => ({
  activeFilterId: getKnmListByPlanCurrentId(state),
  mspCategoriesSelectOptions: getCatalogsMspCategoriesSelectOptions(state),
  currentFilter: getKnmListByPlanCurrent(state),
  eventTypesSelectOptions: getDictionariesAllEventTypesSelectOptions(state),
  filters: getKnmListByPlanFilters(state),
  knmKindsSelectOptions: getDictionariesAllKnmKindsSelectOptions(state),
  objectSubKindsSelectOptions: getKnmListByPlanSubjectSubKindsSelectOptions(state),
  objectKindsSelectOptions: getKnmListByPlanSubjectKindsSelectOptions(state),
  objectTypesSelectOptions: getKnmListByPlanSubjectTypesSelectOptions(state),
  riskCategoriesSelectOptions: getCatalogRiskCategoriesSelectOptions(state),
  supervisionTypesSelectOptions: getKnmListByPlanSupervisionTypesSelectOptions(state),
});

export const KnmListByPlanFiltersModal = connect(mapStateToProps)(KnmListByPlanFiltersModalWithoutStore);
